import React from 'react';
import CustomSwitch from '@/components/CostomSwitch/CustomSwitch';
import LoadingV2 from '@/components/Loading';
import appointmentStyles from '@/ctComponents/AppointmentTable/AppointmentTable.module.css';
import {
  AutobookRuleFormWizardPanel,
  AutobookRuleFormWizardToolbar,
  useAutobookRuleWizard,
} from './AutobookRuleFormSteps';

const inputStyle = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  fontSize: 14,
  fontFamily: "'Averta', sans-serif",
};

const ruleTopFieldLabelStyle = { fontSize: 14 };

/** عرض دقیق ۸۰٪ / ۲۰٪ روی همان divی که label + input داخلش است (مثل چیزی که در Inspect تنظیم می‌کنی). */
const ruleNameFieldShellStyle = {
  boxSizing: 'border-box',
  width: '80%',
  flex: '0 0 80%',
  minWidth: 0,
};

const priorityFieldShellStyle = {
  boxSizing: 'border-box',
  width: '20%',
  flex: '0 0 20%',
  minWidth: 0,
};

const FORM_SCROLL_MAX_HEIGHT_PX = 580;

export const AutobookRuleFormBody = ({
  mode = 'add',
  onSubmit,
  submitLoading = false,
  submitDisabled = false,
  draftLoading = false,
  ruleName,
  setRuleName,
  priority,
  setPriority,
  ruleActive,
  setRuleActive,
  autoEnableAutobook,
  setAutoEnableAutobook,
  deliveryTypeOption,
  setDeliveryTypeOption,
  maximumDaysAfterWindow,
  setMaximumDaysAfterWindow,
  deliveryTypeOptions,
  draftCustomers,
  draftWarehouses,
  draftWarehouseGroups,
  selectedWarehouseCatalogIds,
  setSelectedWarehouseCatalogIds,
  selectedGroupCatalogIds,
  setSelectedGroupCatalogIds,
  draftRuleUuid,
  liveUnloadTime,
  setLiveUnloadTime,
  liveUnloadDriveToPortMin,
  setLiveUnloadDriveToPortMin,
  liveUnloadAutobookBackwardWindowHour,
  setLiveUnloadAutobookBackwardWindowHour,
  liveUnloadAutobookForwardWindowHour,
  setLiveUnloadAutobookForwardWindowHour,
  rulePreferences,
  onRulePreferencesChange,
  swRulePreferences,
  onSwRulePreferencesChange,
  preferencesSyncKey,
  swUseOldestEmptyFirst,
  setSwUseOldestEmptyFirst,
  swSameGroup,
  setSwSameGroup,
  swSameCity,
  setSwSameCity,
  swMaxDistance,
  setSwMaxDistance,
  swMinExcessThreshold,
  setSwMinExcessThreshold,
  swBeforeTargerReturn,
  setSwBeforeTargerReturn,
  swAfterTargerReturn,
  setSwAfterTargerReturn,
  selectedSwSharingGroupCatalogIds,
  setSelectedSwSharingGroupCatalogIds,
}) => {
  const wizard = useAutobookRuleWizard({
    mode,
    onSubmit,
    submitLoading,
    submitDisabled,
    getStepCanProceed: () => !draftLoading,
  });

  return (
    <div className="box-border flex min-h-[400px] w-full min-w-0 max-w-full flex-col">
      <div
        className="relative box-border min-h-0 w-full min-w-0 flex-1"
        style={{ minHeight: 320 }}>
        <div
          className={`box-border w-full min-w-0 max-w-full overflow-x-hidden overflow-y-auto ${
            draftLoading ? 'pointer-events-none select-none' : ''
          }`}
          style={{
            maxHeight: FORM_SCROLL_MAX_HEIGHT_PX,
            boxSizing: 'border-box',
            paddingRight: 6,
          }}
          aria-busy={draftLoading}>
          <div className="box-border flex w-full min-w-0 max-w-full flex-col gap-6 pb-4 pt-5 text-secondary-text">
            <div className="flex w-full min-w-0 max-w-full flex-nowrap items-start">
              <div
                className="flex max-w-full flex-col gap-2 pr-2"
                style={ruleNameFieldShellStyle}>
                <span
                  className={'font-averta mb-2 block text-sm font-bold text-primary-text'}
                  style={ruleTopFieldLabelStyle}>
                  Rule Name
                </span>
                <input
                  className={appointmentStyles.LicenseInput}
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="Enter Rule Name"
                  style={{ ...inputStyle, width: '100%', maxWidth: '100%' }}
                />
              </div>
              <div
                className="flex max-w-full flex-col gap-2"
                style={priorityFieldShellStyle}>
                <span
                  className={'font-averta mb-2 block text-sm font-bold text-primary-text'}
                  style={ruleTopFieldLabelStyle}>
                  Priority
                </span>
                <input
                  type="number"
                  className={appointmentStyles.LicenseInput}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  placeholder="Priority"
                  min={0}
                  style={{ ...inputStyle, width: '100%', maxWidth: '100%' }}
                />
              </div>
            </div>

            <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-4">
              <div className="flex shrink-0 items-center gap-2">
                <CustomSwitch
                  checked={ruleActive}
                  onChange={(next) => setRuleActive(next)}
                />
                <span className="text-sm font-bold leading-snug text-[#131517]">
                  Rule Active
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <CustomSwitch
                  checked={autoEnableAutobook}
                  onChange={(next) => setAutoEnableAutobook(next)}
                />
                <span className="text-sm font-bold leading-snug text-[#131517]">
                  Automatically Enable Autobook
                </span>
              </div>
            </div>

            <AutobookRuleFormWizardPanel
            activeStep={wizard.activeStep}
            setActiveStep={wizard.setActiveStep}
            submitLoading={wizard.submitLoading || draftLoading}
            deliveryTypeOption={deliveryTypeOption}
            setDeliveryTypeOption={setDeliveryTypeOption}
            maximumDaysAfterWindow={maximumDaysAfterWindow}
            setMaximumDaysAfterWindow={setMaximumDaysAfterWindow}
            deliveryTypeOptions={deliveryTypeOptions}
            draftCustomers={draftCustomers}
            draftWarehouses={draftWarehouses}
            draftWarehouseGroups={draftWarehouseGroups}
            selectedWarehouseCatalogIds={selectedWarehouseCatalogIds}
            setSelectedWarehouseCatalogIds={setSelectedWarehouseCatalogIds}
            selectedGroupCatalogIds={selectedGroupCatalogIds}
            setSelectedGroupCatalogIds={setSelectedGroupCatalogIds}
            draftRuleUuid={draftRuleUuid}
            liveUnloadTime={liveUnloadTime}
            setLiveUnloadTime={setLiveUnloadTime}
            liveUnloadDriveToPortMin={liveUnloadDriveToPortMin}
            setLiveUnloadDriveToPortMin={setLiveUnloadDriveToPortMin}
            liveUnloadAutobookBackwardWindowHour={
              liveUnloadAutobookBackwardWindowHour
            }
            setLiveUnloadAutobookBackwardWindowHour={
              setLiveUnloadAutobookBackwardWindowHour
            }
            liveUnloadAutobookForwardWindowHour={
              liveUnloadAutobookForwardWindowHour
            }
            setLiveUnloadAutobookForwardWindowHour={
              setLiveUnloadAutobookForwardWindowHour
            }
            rulePreferences={rulePreferences}
            onRulePreferencesChange={onRulePreferencesChange}
            swRulePreferences={swRulePreferences}
            onSwRulePreferencesChange={onSwRulePreferencesChange}
            preferencesSyncKey={preferencesSyncKey}
            swUseOldestEmptyFirst={swUseOldestEmptyFirst}
            setSwUseOldestEmptyFirst={setSwUseOldestEmptyFirst}
            swSameGroup={swSameGroup}
            setSwSameGroup={setSwSameGroup}
            swSameCity={swSameCity}
            setSwSameCity={setSwSameCity}
            swMaxDistance={swMaxDistance}
            setSwMaxDistance={setSwMaxDistance}
            swMinExcessThreshold={swMinExcessThreshold}
            setSwMinExcessThreshold={setSwMinExcessThreshold}
            swBeforeTargerReturn={swBeforeTargerReturn}
            setSwBeforeTargerReturn={setSwBeforeTargerReturn}
            swAfterTargerReturn={swAfterTargerReturn}
            setSwAfterTargerReturn={setSwAfterTargerReturn}
            selectedSwSharingGroupCatalogIds={selectedSwSharingGroupCatalogIds}
            setSelectedSwSharingGroupCatalogIds={
              setSelectedSwSharingGroupCatalogIds
            }
            />
          </div>
        </div>

        {draftLoading && (
          <div
            className="absolute inset-0 z-[5] flex flex-col items-center justify-center rounded-lg backdrop-blur-sm"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)' }}
            role="status"
            aria-live="polite"
            aria-label="Loading rule data">
            <LoadingV2 />
          </div>
        )}
      </div>

      <div
        className="box-border w-full min-w-0 max-w-full shrink-0 bg-white pt-4"
        style={{ marginTop: 15 }}>
        <AutobookRuleFormWizardToolbar
          submitLoading={wizard.submitLoading || draftLoading}
          primaryLabel={wizard.primaryLabel}
          primaryDisabled={wizard.primaryDisabled}
          handlePrimaryClick={wizard.handlePrimaryClick}
        />
      </div>
    </div>
  );
};
